import React from 'react';

import Container from 'react-bootstrap/Container';
import Row from 'react-bootstrap/Row';
import Col from 'react-bootstrap/Col';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faAngleLeft, faAngleRight } from '@fortawesome/free-solid-svg-icons';

import ArticleGridView from './components/article-grid'; 
import ArticleView from './components/article-view'; 
import ArticleList from './components/article-list'; 
import Filters from './components/Filters';

import { 
  updateArticleHandler,
} from './globals';

import {
  selectArticleFromList,
  ArticleInterface,
  deselectArticle,
  filterByTitleLetter,
  updateArticle,
  Articles,
  isEditingEnabled,
  simpleRouter,
  loadArticleList,
  stageArticleList,
  getArticleById,
  ArticlesInterface,
} from './components/page_data'; 

import './App.scss';

const DATA_STORE_ELEMENT_SELECTOR = '#dataStore';
const DATA_STORE_SNIPPET_SELECTOR = '#dataStore > .cke_editable';

type AppState = {
  articleList: Articles | null,
  filteredArticles: Array<ArticleInterface>,
  selectedLetter: string,
  sidePanelToggled: boolean,
  editMode: boolean,
}

class App extends React.Component<{}, AppState> {
  public handleArticleSelect: (id: string) => void
  public updateSelectedLetter: (letter: string) => void
  public handleSidePanelToggle: (e: React.SyntheticEvent) => void
  public clickViewAllHandler: (arg0: React.SyntheticEvent) => void
  public addNewArticle: () => void
  public updateStore: () => void
  public deleteArticle: (id: string) => void
  public updateVideoUrl: updateArticleHandler
  public updateContent: updateArticleHandler
  public updateImage: updateArticleHandler
  public updateTitle: updateArticleHandler
  public toggleFullWidth: updateArticleHandler<boolean>
  public toggleImageDisplay: updateArticleHandler<boolean>

  constructor(props: {}){
    super(props);
    const genericArticleUpdate:<K extends keyof ArticleInterface> (
      key: K,
      value: ArticleInterface[K],
      articleId?: string,
      transform?: (arg0: ArticleInterface[K])=>ArticleInterface[K],
    ) => void = (key, value, articleId, transform) => {
      const { articleList } = this.state;
      if (articleList){
        const { selectedArticle } = articleList;
        const article = (
          articleId
            ? getArticleById(articleId, articleList.articles) 
            : selectedArticle
          )
        if (article){
          articleList.selectedArticle = updateArticle(
            article, key, value, transform
          );
          this.updateStore();
        }
      }
    }

    const removeHtmlTags = (url: string): string => {
      const reHtmlTags = /<\/*.+?>/ig;
      return url.replace(reHtmlTags, '').trim();
    }

    this.state = {
      articleList: null,
      filteredArticles: [],
      selectedLetter: '',
      sidePanelToggled: false,
      editMode: false,
    };

    this.handleArticleSelect = (id: string): void => {
      const { articleList } = this.state;
      if (articleList){
        selectArticleFromList(id, articleList);
        this.setState({articleList: articleList});
      }
    };

    // TODO rename to updateLetterFilter
    this.updateSelectedLetter = (letter: string): void => {
      const { articleList } = this.state;
      if (articleList){
        const filteredArticles = filterByTitleLetter(
          letter, articleList.articles
        );
        this.setState({ filteredArticles, selectedLetter: letter });
      }
    }

    this.handleSidePanelToggle = (e: React.SyntheticEvent): void => {
      let { sidePanelToggled } = this.state;
      sidePanelToggled = !sidePanelToggled;

      this.setState({sidePanelToggled})
    }

    this.clickViewAllHandler = () => {
      const { articleList } = this.state;
      if (articleList) {
        deselectArticle(articleList);
        this.setState({filteredArticles: articleList.articles})
      }
    }

    this.updateVideoUrl = (url: string, id?: string) => {
      genericArticleUpdate('videoUrl', url, id, removeHtmlTags);
    }

    this.toggleFullWidth = (fullWidth: boolean, id?: string): void => {
      genericArticleUpdate('fullWidthImage', fullWidth, id);
    }

    this.toggleImageDisplay = (display: boolean, id?: string): void => {
      genericArticleUpdate('displayImage', display, id);
    }

    this.updateContent = (content: string, id?: string) => {
      genericArticleUpdate('content', content);
    }

    this.deleteArticle = (id: string) => {
      const { articleList } = this.state;
      if (articleList) {
        articleList.deleteArticle(id);
        this.updateStore();
      }
    }

    this.updateTitle = (title: string, id?: string) => {
      genericArticleUpdate('title', title, id);
    }

    this.updateStore = () => {
      const { editMode, articleList} = this.state;
      if (articleList) {
        const dataStoreSelector = (
          editMode
            ? DATA_STORE_SNIPPET_SELECTOR
            : DATA_STORE_ELEMENT_SELECTOR
        );

        stageArticleList(articleList.articles, dataStoreSelector);
        this.setState({ articleList });
      }
    }

    this.addNewArticle = (): void =>  {
      const { articleList } = this.state;
      if (articleList) {
        articleList.createNewArticle()
        this.updateStore();
      }
    }

    this.updateImage = (imageContent: string, id?: string) => {
      genericArticleUpdate('imageSrc', imageContent, id);
    }
  }

  componentDidMount (){
      let editMode = isEditingEnabled();
      const dataStoreSelector =  DATA_STORE_ELEMENT_SELECTOR;
   
      const articles = loadArticleList(dataStoreSelector) || [];
      const articleList = new Articles(articles);
      const search = simpleRouter(window.location.href);
      const { selectedArticleId, selectedLetter } = search;
   
      if (selectedArticleId){
        selectArticleFromList(selectedArticleId, articleList)
      }
   
      this.setState({ 
        editMode,
        selectedLetter: (selectedLetter || ""),
        articleList,
        filteredArticles: articleList.articles,
      });
  }

  render(): React.ReactNode {
    const { 
      state, 
      updateVideoUrl, 
      updateContent, 
      updateImage, 
      updateTitle,
      handleArticleSelect,
      addNewArticle,
      deleteArticle,
      toggleImageDisplay,
      toggleFullWidth,
    } = this;

    const {
      articleList, 
      selectedLetter, 
      sidePanelToggled, 
      filteredArticles, 
      editMode,
    } = state;

    if (articleList) {
      var { selectedArticle } = articleList;
    }

    function isGridOnlyView() {
      return false;
    }

    return (
      <Container
        fluid={false}
        as="div"
        className={editMode && 'editing'}
      >
        <Row>
          <Col xs={{ span: 12 }}>
            <Filters
              isGridOnlyView={isGridOnlyView()}
              selectedLetter={selectedLetter}
              clickViewAllHandler={this.clickViewAllHandler}
              updateSelectedLetter={this.updateSelectedLetter}
            />
          </Col>
        </Row>
        <Row>
          {
            !isGridOnlyView()
            ? <Col
                xs={{ span: 10}}
                md={{ span: 3}}
                className={`side-panel p-0 ${sidePanelToggled ? '': 'side-panel--hidden'}`}
              >
                <button
                  className="side-panel__toggle btn btn-link shadow"
                  onClick={this.handleSidePanelToggle}
                >
                  <FontAwesomeIcon
                    icon={sidePanelToggled ? faAngleLeft: faAngleRight}
                  />
                </button>
                <ArticleList
                  title={selectedLetter.toUpperCase()}
                  listItems={filteredArticles}
                  btnClickHandler={this.handleArticleSelect}
                />
              </Col>
            : ''
          }
          <Col
            xs={{ span: 12 }}
            md={{ span: isGridOnlyView() ? 12 : 9 }}
            className="pl-md-4"
          >
            <Row
              className=""
              as="section"
            >
              { selectedArticle 
                ? <ArticleView
                    article={selectedArticle}
                    editMode={editMode}
                    onVideoUrlChange={updateVideoUrl}
                    onBodyContentChange={updateContent}
                    onFeatureImageChange={updateImage}
                    onTitleChange={updateTitle}
                    toggleFullWidth={toggleFullWidth}
                    toggleImageDisplay={toggleImageDisplay}
                  />
                : <ArticleGridView
                    editMode={editMode}
                    articles={filteredArticles}
                    btnClickHandler={handleArticleSelect}
                    deleteBtnHandler={deleteArticle}
                    addBtnClickHandler={addNewArticle}
                  />
              }
            </Row>
          </Col>
        </Row>
      </Container>
    );
  }
}

export default App;